// Base de l'API Worker. En dev : `wrangler dev` sur http://127.0.0.1:8787.
// En prod : configurer un sous-domaine du même domaine que le front
// (ex: app.example.com pour Pages, api.example.com pour le Worker) afin que
// le cookie de session (SameSite=Lax) soit bien envoyé sur les requêtes fetch.
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://127.0.0.1:8787";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any).message ?? (body as any).error ?? `Erreur HTTP ${res.status}`);
  }
  return res.json();
}

export interface SessionUser {
  id: string;
  discordId: string;
  username: string;
  avatarUrl: string | null;
  role: "reader" | "contributor" | "admin";
  isGuildMember: boolean;
  hasRequiredRole: boolean;
}

export const api = {
  me: () => request<{ user: SessionUser | null }>("/api/auth/me"),
  loginUrl: () => `${API_BASE_URL}/api/auth/discord`,
  logout: () => request("/api/auth/logout", { method: "POST" }),

  listProjects: () => request<{ projects: any[] }>("/api/projects"),
  getProject: (id: string) => request<{ project: any; translations: any[] }>(`/api/projects/${id}`),
  discoverProject: (repoUrl: string, branch?: string) =>
    request<{ owner: string; repo: string; branch: string; headSha: string; candidates: any[] }>(
      "/api/projects/discover",
      { method: "POST", body: JSON.stringify({ repoUrl, branch }) }
    ),
  addProject: (payload: {
    repoUrl: string;
    branch?: string;
    jsonPath: string;
    name: string;
    description?: string;
  }) => request<{ id: string }>("/api/projects", { method: "POST", body: JSON.stringify(payload) }),
  syncProject: (id: string) =>
    request<{ updated: boolean; sha: string }>(`/api/projects/${id}/sync`, { method: "POST" }),
  setDriveFolder: (id: string, folder: string) =>
    request<{ ok: boolean; folderId: string | null; filesIndexed?: number; warning?: string }>(
      `/api/projects/${id}/drive-folder`,
      { method: "PUT", body: JSON.stringify({ folder }) }
    ),
  refreshDriveFolder: (id: string) =>
    request<{ ok: boolean; filesIndexed: number }>(`/api/projects/${id}/drive-folder/refresh`, { method: "POST" }),

  listCards: (projectId: string) =>
    request<{ cards: any[]; encounterSets: any[] }>(`/api/projects/${projectId}/cards`),
  getCard: (projectId: string, cardId: string, lang?: string) =>
    request<{ card: any; translation: any | null; translationId?: string | null }>(
      `/api/projects/${projectId}/cards/${cardId}${lang ? `?lang=${encodeURIComponent(lang)}` : ""}`
    ),

  setOriginalField: (
    projectId: string,
    cardId: string,
    face: "front" | "back",
    field: string,
    value: string
  ) =>
    request(`/api/projects/${projectId}/cards/${cardId}/${face}/${field}`, {
      method: "PUT",
      body: JSON.stringify({ value }),
    }),

  listTranslations: (projectId: string) =>
    request<{ translations: any[] }>(`/api/projects/${projectId}/translations`),
  /** Récupère le fichier de traduction complet — le parsing JSON a lieu dans
   * le navigateur (via `request`/`res.json()`), jamais sur le Worker, pour
   * ne pas dépasser son budget CPU sur un gros fichier. À mettre en cache
   * côté appelant : ne pas rappeler à chaque changement de carte. */
  getTranslationRaw: (projectId: string, lang: string) =>
    request<any>(`/api/projects/${projectId}/translations/${lang}/raw`),
  addTranslation: (projectId: string, langCode: string) =>
    request<{ id: string; langCode: string }>(`/api/projects/${projectId}/translations`, {
      method: "POST",
      body: JSON.stringify({ langCode }),
    }),
  importTranslation: (projectId: string, langCode: string, content: unknown) =>
    request<{ id: string; langCode: string; replaced: boolean }>(
      `/api/projects/${projectId}/translations/import`,
      { method: "POST", body: JSON.stringify({ langCode, content }) }
    ),
  setTranslatedField: (
    projectId: string,
    lang: string,
    cardId: string,
    face: "front" | "back",
    field: string,
    value: string
  ) =>
    request(`/api/projects/${projectId}/translations/${lang}/cards/${cardId}/${face}/${field}`, {
      method: "PUT",
      body: JSON.stringify({ value }),
    }),
  setTranslatedName: (projectId: string, lang: string, cardId: string, value: string) =>
    request(`/api/projects/${projectId}/translations/${lang}/cards/${cardId}/name`, {
      method: "PUT",
      body: JSON.stringify({ value }),
    }),

  setTranslationStatus: (projectId: string, lang: string, status: "draft" | "in_review" | "published") =>
    request<{ ok: boolean; status: string }>(`/api/projects/${projectId}/translations/${lang}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),

  listHistory: (projectId: string, cardId: string, lang?: string) =>
    request<{ edits: any[] }>(
      `/api/projects/${projectId}/cards/${cardId}/history${lang ? `?lang=${encodeURIComponent(lang)}` : ""}`
    ),

  listReviewStatuses: (projectId: string, lang?: string) =>
    request<{ statuses: Record<string, string> }>(
      `/api/projects/${projectId}/review-status${lang ? `?lang=${encodeURIComponent(lang)}` : ""}`
    ),
  getReviewStatus: (projectId: string, cardId: string, lang?: string) =>
    request<{ status: string }>(
      `/api/projects/${projectId}/cards/${cardId}/status${lang ? `?lang=${encodeURIComponent(lang)}` : ""}`
    ),
  setReviewStatus: (
    projectId: string,
    cardId: string,
    status: "unreviewed" | "reviewed" | "needs_correction",
    lang?: string
  ) =>
    request<{ ok: boolean; status: string }>(`/api/projects/${projectId}/cards/${cardId}/status`, {
      method: "PUT",
      body: JSON.stringify({ status, lang }),
    }),

  listNotes: (projectId: string, cardId: string, lang?: string) =>
    request<{ notes: any[] }>(
      `/api/projects/${projectId}/cards/${cardId}/notes${lang ? `?lang=${encodeURIComponent(lang)}` : ""}`
    ),
  addNote: (
    projectId: string,
    cardId: string,
    payload: { content: string; face?: string; langCode?: string }
  ) =>
    request(`/api/projects/${projectId}/cards/${cardId}/notes`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  resolveNote: (noteId: string, resolved: boolean) =>
    request(`/api/projects/notes/${noteId}`, { method: "PATCH", body: JSON.stringify({ resolved }) }),

  exportProjectUrl: (projectId: string, lang?: string) =>
    `${API_BASE_URL}/api/projects/${projectId}/export${lang ? `?lang=${encodeURIComponent(lang)}` : ""}`,

  requestRender: (
    projectId: string,
    payload: { cardId: string; lang?: string | null; format?: "jpeg" | "png"; bleed?: 0 | 1 }
  ) =>
    request<{
      front: { cacheKey: string; status: string; url?: string };
      back: { cacheKey: string; status: string; url?: string };
    }>(`/api/projects/${projectId}/render`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  renderStatus: (cacheKey: string) =>
    request<{ status: string; url?: string }>(`/api/projects/status/${cacheKey}`),

  admin: {
    listUsers: () => request<{ users: any[] }>("/api/admin/users"),
    setUserRole: (userId: string, role: "reader" | "contributor" | "admin") =>
      request(`/api/admin/users/${userId}/role`, { method: "PATCH", body: JSON.stringify({ role }) }),
    deleteProject: (projectId: string) =>
      request(`/api/admin/projects/${projectId}`, { method: "DELETE" }),
    getR2Status: () => request<{ status: any }>("/api/admin/r2/status"),
    reconcileR2: () => request<{ status: any }>("/api/admin/r2/reconcile", { method: "POST" }),
    setR2Override: (override: "none" | "forced_frozen" | "forced_unfrozen") =>
      request("/api/admin/r2/override", { method: "POST", body: JSON.stringify({ override }) }),
  },
};
